/**
 * Shared style constants for the table widget.
 * Controls both the grid (Ant Design) and PDF (@react-pdf/renderer) tables
 * from a single source of truth.
 *
 * These values match the team-page table (`.team-table` in styles-ResultsTable.css)
 * so the dashboard table widget looks identical.
 *
 * Keys suffixed with _GRID or _PDF denote platform-specific overrides.
 * Keys without a suffix are shared across both platforms.
 */

/** Body cell vertical padding in CSS pixels (top + bottom each). */
export const TABLE_CELL_PAD_V_PX = 3;

/** Header cell vertical padding in CSS pixels (top + bottom each). */
export const TABLE_HEADER_PAD_V_PX = 2;

/** Body cell line-height in CSS pixels. */
export const TABLE_BODY_LINE_HEIGHT_PX = 15;

/** Header cell line-height in CSS pixels. */
export const TABLE_HEADER_LINE_HEIGHT_PX = 20;

/**
 * Effective row height in CSS pixels.
 * body: padV*2 + lineHeight = 3*2 + 15 = 21
 * header: padV*2 + lineHeight = 2*2 + 20 = 24
 * The PDF uses the body row height for layout calculations.
 */
export const TABLE_ROW_HEIGHT_PX = TABLE_CELL_PAD_V_PX * 2 + TABLE_BODY_LINE_HEIGHT_PX;

/** Body font size in CSS pixels. */
export const TABLE_FONT_SIZE_PX = 14;

/** Title bar height in CSS pixels (fontSize 13 + vertical padding). */
export const TABLE_TITLE_HEIGHT_PX = 21;

/** Maximum width of the category (first) column in CSS pixels. */
export const TABLE_CATEGORY_COL_MAX_WIDTH_PX = 200;

/** Minimum width of the category column for name-based group-bys (player, position, drill). */
export const TABLE_CATEGORY_NAME_MIN_WIDTH_PX = 120;

/** Group-by values that use names (wider category column). */
export const NAME_GROUP_BYS = new Set(["player", "position", "drill"]);

/**
 * Sort key for the category column. Name group-bys (player/position/drill)
 * sort by the visible display label so order matches the names the user sees;
 * the raw category there is a non-name key like "Player 42". Date group-bys
 * sort by the raw ISO category because the display label has its year stripped.
 */
export function categorySortKey(row: Record<string, unknown>, groupBy: string | undefined): string {
  return NAME_GROUP_BYS.has(groupBy ?? "")
    ? String(row.category ?? row.__raw_category ?? "")
    : String(row.__raw_category ?? row.category ?? "");
}

/** Cell horizontal padding in CSS pixels (shared). */
export const TABLE_CELL_PAD_H_PX = 4;

/** Average row border weight in CSS pixels. */
export const TABLE_AVG_ROW_BORDER_PX = 2;

/** PDF-specific: outer cell padding scale factor (multiplied by pdfScale). */
export const TABLE_OUTER_PAD_FACTOR_PDF = 4;
